/* Chavu — global.js */
(() => {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Utilities                                                          */
  /* ------------------------------------------------------------------ */
  const FOCUSABLE = 'summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^="-"]), input:not([type="hidden"]):not([disabled]), select, textarea';

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
  }

  function trapFocus(container, initial) {
    removeTrapFocus();
    const elements = getFocusable(container);
    const first = elements[0];
    const last = elements[elements.length - 1];
    (initial || first || container).focus();

    trapFocus.handler = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = getFocusable(container);
      if (!focusable.length) return;
      const firstEl = focusable[0];
      const lastEl = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener('keydown', trapFocus.handler);
  }

  function removeTrapFocus(target) {
    if (trapFocus.handler) document.removeEventListener('keydown', trapFocus.handler);
    trapFocus.handler = null;
    if (target) target.focus();
  }

  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  }

  window.chavu = { trapFocus, removeTrapFocus, debounce, fetchConfig };

  /* ------------------------------------------------------------------ */
  /* Drawer base                                                        */
  /* ------------------------------------------------------------------ */
  class DrawerComponent extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('keyup', (e) => e.key === 'Escape' && this.close());
      // Delegate so close still works after cart drawer HTML swap (add-to-cart)
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target.closest('.drawer__overlay')) {
          this.close();
        }
      });
    }

    open(opener) {
      this.opener = opener || this.opener;
      this.classList.add('active');
      this.removeAttribute('aria-hidden');
      document.body.classList.add('overflow-hidden');
      const panel = this.querySelector('.drawer__panel') || this;
      setTimeout(() => trapFocus(panel, this.querySelector('[data-drawer-close]')), 50);
      this.dispatchEvent(new CustomEvent('drawer:open'));
    }

    close() {
      this.classList.remove('active');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus(this.opener);
      this.dispatchEvent(new CustomEvent('drawer:close'));
    }
  }
  customElements.define('drawer-component', DrawerComponent);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-target]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.dataset.drawerTarget);
    if (drawer && typeof drawer.open === 'function') {
      event.preventDefault();
      drawer.open(trigger);
    }
  });

  /* ------------------------------------------------------------------ */
  /* Menu drawer accordion                                              */
  /* ------------------------------------------------------------------ */
  document.querySelectorAll('[data-submenu-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const item = btn.closest('.menu-drawer__item');
      const isOpen = item.dataset.open === 'true';
      item.dataset.open = String(!isOpen);
      btn.setAttribute('aria-expanded', String(!isOpen));
    });
  });

  /* ------------------------------------------------------------------ */
  /* Desktop header submenus                                            */
  /* ------------------------------------------------------------------ */
  class HeaderMenus {
    constructor(header) {
      this.header = header;
      this.triggers = header.querySelectorAll('[data-menu-trigger]');
      this.closeTimer = null;
      this.triggers.forEach((trigger) => {
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!panel) return;
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.isOpen(panel) ? this.close(panel, trigger) : this.open(panel, trigger);
        });
        trigger.addEventListener('mouseenter', () => this.open(panel, trigger));
        trigger.addEventListener('mouseleave', () => this.scheduleClose(panel, trigger));
        panel.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));
        panel.addEventListener('mouseleave', () => this.scheduleClose(panel, trigger));
        panel.addEventListener('keyup', (e) => e.key === 'Escape' && this.close(panel, trigger, true));
      });
      document.addEventListener('click', (e) => {
        if (!this.header.contains(e.target)) this.closeAll();
      });
    }

    isOpen(panel) {
      return panel.dataset.open === 'true';
    }

    open(panel, trigger) {
      clearTimeout(this.closeTimer);
      this.closeAll();
      panel.dataset.open = 'true';
      trigger.setAttribute('aria-expanded', 'true');
    }

    close(panel, trigger, refocus) {
      panel.dataset.open = 'false';
      trigger.setAttribute('aria-expanded', 'false');
      if (refocus) trigger.focus();
    }

    scheduleClose(panel, trigger) {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.close(panel, trigger), 200);
    }

    closeAll() {
      this.triggers.forEach((trigger) => {
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (panel) this.close(panel, trigger);
      });
    }
  }

  document.querySelectorAll('header-menus').forEach((el) => new HeaderMenus(el));

  /* ------------------------------------------------------------------ */
  /* Sticky header                                                      */
  /* ------------------------------------------------------------------ */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.querySelector('.header-wrapper');
      this.mode = this.dataset.sticky || 'none';
      if (this.mode === 'none' || !this.wrapper) return;
      const section = this.closest('.shopify-section') || this;
      section.classList.add('shopify-section-header-sticky');
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    onScroll() {
      const y = window.scrollY;
      this.wrapper.classList.toggle('header-wrapper--scrolled', y > 40);
      if (this.mode === 'on-scroll-up') {
        if (y > this.lastY && y > 200) {
          this.wrapper.classList.add('header-wrapper--hidden');
        } else {
          this.wrapper.classList.remove('header-wrapper--hidden');
        }
      }
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ------------------------------------------------------------------ */
  /* Quantity input                                                     */
  /* ------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const prev = this.input.value;
          btn.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
          if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------ */
  /* Cart drawer                                                        */
  /* ------------------------------------------------------------------ */
  class CartDrawer extends DrawerComponent {
    renderContents(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('[data-cart-contents]');
      const current = this.querySelector('[data-cart-contents]');
      if (fresh && current) current.replaceWith(fresh);
      updateCartCount(this);
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  function updateCartCount(scope) {
    const contents = (scope || document).querySelector('[data-cart-contents]');
    if (!contents) return;
    const count = contents.dataset.cartCount;
    document.querySelectorAll('[data-cart-count-bubble]').forEach((bubble) => {
      bubble.textContent = count === '0' ? '' : count;
      bubble.classList.toggle('visually-hidden', count === '0');
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cart items (drawer + page)                                         */
  /* ------------------------------------------------------------------ */
  class CartItems extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-quantity-input]')) {
          this.updateQuantity(event.target.dataset.index, event.target.value);
        }
      });
      this.addEventListener('click', (event) => {
        const removeBtn = event.target.closest('[data-remove-item]');
        if (removeBtn) {
          event.preventDefault();
          this.updateQuantity(removeBtn.dataset.index, 0);
        }
      });
    }

    updateQuantity(line, quantity) {
      this.classList.add('cart-loading');
      const body = JSON.stringify({
        line,
        quantity,
        sections: [this.sectionId],
        sections_url: window.location.pathname,
      });

      fetch(window.themeRoutes.cart_change_url, { ...fetchConfig(), body })
        .then((response) => response.json())
        .then((state) => {
          if (state.errors) {
            this.showError(state.errors);
            return;
          }
          const html = state.sections[this.sectionId];
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const fresh = parsed.querySelector('[data-cart-contents]');
          const current = document.querySelector(`[data-cart-contents][data-section-id="${this.sectionId}"]`)
            || this.closest('[data-cart-contents]')
            || document.querySelector('[data-cart-contents]');
          if (fresh && current) current.replaceWith(fresh);
          updateCartCount();
          document.dispatchEvent(new CustomEvent('cart:updated'));
        })
        .catch(() => this.showError(window.themeStrings.cartError))
        .finally(() => this.classList.remove('cart-loading'));
    }

    showError(message) {
      const errorEl = document.querySelector('[data-cart-errors]');
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.removeAttribute('hidden');
        setTimeout(() => errorEl.setAttribute('hidden', ''), 5000);
      }
    }
  }
  customElements.define('cart-items', CartItems);

  /* ------------------------------------------------------------------ */
  /* Product form (AJAX add to cart)                                    */
  /* ------------------------------------------------------------------ */
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.submitButton = this.querySelector('[type="submit"]');
      this.errorWrapper = this.querySelector('.product-form__error');
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleError(false);
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');

      const drawer = document.querySelector('cart-drawer');
      const formData = new FormData(this.form);
      if (drawer) formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);

      fetch(window.themeRoutes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleError(response.description || response.message);
            return;
          }
          if (drawer && window.themeSettings.cartType === 'drawer') {
            if (response.sections && response.sections['cart-drawer']) {
              drawer.renderContents(response.sections['cart-drawer']);
            }
            drawer.open(this.submitButton);
            document.dispatchEvent(new CustomEvent('cart:updated'));
          } else {
            window.location = window.themeRoutes.cart_url;
          }
        })
        .catch(() => this.handleError(window.themeStrings.cartError))
        .finally(() => {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
        });
    }

    handleError(message) {
      if (!this.errorWrapper) return;
      if (!message) {
        this.errorWrapper.setAttribute('hidden', '');
        this.errorWrapper.textContent = '';
        return;
      }
      this.errorWrapper.removeAttribute('hidden');
      this.errorWrapper.textContent = message;
    }
  }
  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------------ */
  /* Sliders                                                            */
  /* ------------------------------------------------------------------ */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('.slider');
      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');
      if (!this.slider) return;
      if (this.prevButton) this.prevButton.addEventListener('click', () => this.scrollBySlide(-1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.scrollBySlide(1));
      this.slider.addEventListener('scroll', debounce(() => this.updateButtons(), 60), { passive: true });
      this.updateButtons();
    }

    slideWidth() {
      const slide = this.slider.querySelector('.slider__slide');
      if (!slide) return this.slider.clientWidth;
      const gap = parseFloat(getComputedStyle(this.slider).columnGap || 0);
      return slide.getBoundingClientRect().width + gap;
    }

    scrollBySlide(direction) {
      this.slider.scrollBy({ left: this.slideWidth() * direction, behavior: 'smooth' });
    }

    updateButtons() {
      if (!this.prevButton || !this.nextButton) return;
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth - 4;
      this.prevButton.disabled = this.slider.scrollLeft <= 4;
      this.nextButton.disabled = this.slider.scrollLeft >= maxScroll;
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ------------------------------------------------------------------ */
  /* Slideshow                                                          */
  /* ------------------------------------------------------------------ */
  class SlideshowComponent extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.slideshow__slide'));
      this.dots = Array.from(this.querySelectorAll('.slideshow__dot'));
      this.index = 0;
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = parseInt(this.dataset.speed || '6', 10) * 1000;
      if (this.slides.length < 2) return;

      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i, true)));
      if (this.autoplay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.start();
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());
      }
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      this.stop();
      this.timer = setInterval(() => this.goTo((this.index + 1) % this.slides.length), this.speed);
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    goTo(index, manual) {
      this.slides[this.index].classList.remove('active');
      if (this.dots[this.index]) this.dots[this.index].setAttribute('aria-current', 'false');
      this.index = index;
      this.slides[this.index].classList.add('active');
      if (this.dots[this.index]) this.dots[this.index].setAttribute('aria-current', 'true');
      if (manual && this.autoplay) this.start();
    }
  }
  customElements.define('slideshow-component', SlideshowComponent);

  /* ------------------------------------------------------------------ */
  /* Deferred media (video)                                             */
  /* ------------------------------------------------------------------ */
  class DeferredMedia extends HTMLElement {
    constructor() {
      super();
      const poster = this.querySelector('[data-deferred-media-poster]');
      if (poster) poster.addEventListener('click', () => this.loadContent());
    }

    loadContent() {
      if (this.getAttribute('loaded')) return;
      const template = this.querySelector('template');
      if (!template) return;
      const content = template.content.firstElementChild.cloneNode(true);
      this.appendChild(content);
      this.setAttribute('loaded', 'true');
      const poster = this.querySelector('[data-deferred-media-poster]');
      if (poster) poster.remove();
      const media = this.querySelector('video, iframe');
      if (media && media.tagName === 'VIDEO') media.play().catch(() => {});
      const modelViewer = this.querySelector('model-viewer');
      if (modelViewer && window.Shopify && window.Shopify.loadFeatures) {
        window.Shopify.loadFeatures([
          {
            name: 'model-viewer-ui',
            version: '1.0',
            onLoad: () => {
              if (window.Shopify.ModelViewerUI) new window.Shopify.ModelViewerUI(modelViewer);
            },
          },
        ]);
      }
    }
  }
  customElements.define('deferred-media', DeferredMedia);

  /* ------------------------------------------------------------------ */
  /* Search modal + predictive search                                   */
  /* ------------------------------------------------------------------ */
  class SearchModal extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.addEventListener('keyup', (e) => e.key === 'Escape' && this.close());
      this.querySelectorAll('[data-search-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      document.addEventListener('click', (e) => {
        if (this.classList.contains('active') && !this.contains(e.target) && !e.target.closest('[data-drawer-target="' + this.id + '"]')) {
          this.close();
        }
      });
    }

    open(opener) {
      this.opener = opener;
      this.classList.add('active');
      document.body.classList.add('overflow-hidden');
      setTimeout(() => this.input && this.input.focus(), 100);
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      if (this.opener) this.opener.focus();
    }
  }
  customElements.define('search-modal', SearchModal);

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-search-results]');
      if (!this.input || !this.results || !window.themeSettings.predictiveSearch) return;
      this.input.addEventListener('input', debounce(() => this.onChange(), 300));
    }

    onChange() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      fetch(`${window.themeRoutes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product,collection,query&resources[limit]=6&section_id=predictive-search`)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          const html = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search');
          this.results.innerHTML = html ? html.innerHTML : '';
        })
        .catch(() => {
          this.results.innerHTML = '';
        });
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------ */
  /* Facets: auto-submit filter forms                                   */
  /* ------------------------------------------------------------------ */
  document.querySelectorAll('[data-filter-form]').forEach((form) => {
    if (form.dataset.autoSubmit !== 'true') return;
    form.addEventListener('change', (event) => {
      if (event.target.matches('select, input[type="checkbox"]')) form.submit();
    });
  });

  document.querySelectorAll('[data-sort-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  });

  /* ------------------------------------------------------------------ */
  /* Localization forms                                                 */
  /* ------------------------------------------------------------------ */
  document.querySelectorAll('.localization-form, [data-localization-form]').forEach((form) => {
    form.querySelectorAll('select').forEach((select) => {
      select.addEventListener('change', () => form.submit());
    });
  });

  /* ------------------------------------------------------------------ */
  /* Customer addresses                                                 */
  /* ------------------------------------------------------------------ */
  class CustomerAddresses extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-address-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const target = document.getElementById(btn.dataset.addressToggle);
          if (!target) return;
          const isHidden = target.hidden;
          target.hidden = !isHidden;
          this.querySelectorAll(`[data-address-toggle="${btn.dataset.addressToggle}"]`).forEach((b) =>
            b.setAttribute('aria-expanded', String(isHidden))
          );
          if (isHidden) {
            const firstInput = target.querySelector('input, select');
            if (firstInput) firstInput.focus();
          }
        });
      });

      this.querySelectorAll('[data-address-delete]').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          if (!window.confirm(btn.dataset.confirmMessage)) {
            event.preventDefault();
          }
        });
      });

      this.querySelectorAll('select[data-address-country]').forEach((select) => this.initCountry(select));
    }

    initCountry(countrySelect) {
      const provinceWrapper = document.getElementById(countrySelect.dataset.provinceWrapper);
      const provinceSelect = document.getElementById(countrySelect.dataset.provinceSelect);
      const defaultCountry = countrySelect.dataset.default;
      if (defaultCountry) {
        const option = Array.from(countrySelect.options).find((o) => o.value === defaultCountry || o.text === defaultCountry);
        if (option) option.selected = true;
      }

      const update = () => {
        const selected = countrySelect.options[countrySelect.selectedIndex];
        const raw = selected ? selected.getAttribute('data-provinces') : null;
        let provinces = [];
        try {
          provinces = raw ? JSON.parse(raw) : [];
        } catch (e) {
          provinces = [];
        }
        if (!provinceWrapper || !provinceSelect) return;
        if (!provinces.length) {
          provinceWrapper.hidden = true;
          provinceSelect.innerHTML = '';
          return;
        }
        provinceWrapper.hidden = false;
        provinceSelect.innerHTML = provinces
          .map((p) => `<option value="${p[0]}">${p[1]}</option>`)
          .join('');
        const defaultProvince = provinceSelect.dataset.default;
        if (defaultProvince) {
          const option = Array.from(provinceSelect.options).find((o) => o.value === defaultProvince || o.text === defaultProvince);
          if (option) option.selected = true;
        }
      };

      countrySelect.addEventListener('change', update);
      update();
    }
  }
  customElements.define('customer-addresses', CustomerAddresses);

  /* ------------------------------------------------------------------ */
  /* Reveal on scroll                                                   */
  /* ------------------------------------------------------------------ */
  function initReveal(scope) {
    if (document.body.classList.contains('no-reveal')) return;
    if (document.documentElement.hasAttribute('data-gsap') && !initReveal.forced) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      (scope || document).querySelectorAll('.reveal').forEach((el) => el.classList.add('revealed'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    (scope || document).querySelectorAll('.reveal:not(.revealed)').forEach((el) => observer.observe(el));
  }

  initReveal();

  window.chavuInitReveal = (scope) => {
    initReveal.forced = true;
    initReveal(scope);
  };

  /* ------------------------------------------------------------------ */
  /* Theme editor support                                               */
  /* ------------------------------------------------------------------ */
  document.addEventListener('shopify:section:load', (event) => {
    initReveal(event.target);
    event.target.querySelectorAll('header-menus').forEach((el) => new HeaderMenus(el));
  });
})();
